import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../components/Flex';
import { Message } from '../../../../models/Message';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import useProjectsContext from '../../../projects/hooks/useProjectsContext';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  sentFromProjectInfo: {
    margin: theme.spacing.small,
    padding: `${theme.spacing.small}px 24px`,
    backgroundColor: theme.colors.base.blue[50],
    color: theme.colors.application.typography.default,
    fontSize: theme.font.fontSize.small,
  },
}));

interface SentFromProjectPanelProps {
  message: Message;
}

export const SentFromProjectPanel: React.FC<SentFromProjectPanelProps> = ({
  message,
}) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const { allProjects } = useProjectsContext();

  const sentFromProject = useMemo(() => {
    const senderEmail = (
      message.sender?.emailAddress?.address ?? ''
    ).toLowerCase();
    const replyToAddresses = message.replyTo
      .map((r) => (r.emailAddress?.address ?? '').toLowerCase())
      .filter(Boolean);

    return allProjects.find((project) => {
      if (!project.eMailPrefix || !project.eMailSuffix) return false;
      const projectEmail =
        `${project.eMailPrefix}@${project.eMailSuffix}`.toLowerCase();
      const hasReplyTo = replyToAddresses.includes(projectEmail);
      const isSender = senderEmail === projectEmail;
      return hasReplyTo || isSender;
    });
  }, [message.replyTo, message.sender?.emailAddress?.address, allProjects]);

  if (!sentFromProject) return null;

  const projectName =
    `${sentFromProject.number ?? ''} ${sentFromProject.shortName ?? sentFromProject.name ?? ''}`.trim();

  return (
    <Flex.Row
      alignItems="center"
      childrenGap={theme.spacing.regular}
      className={classes.sentFromProjectInfo}
    >
      <FontAwesomeIcon icon={['fal', 'paper-plane']} />
      <Flex.Item flex={1}>
        {t('mail:sentFromProjectPanel.description', { projectName })}
      </Flex.Item>
    </Flex.Row>
  );
};
